import * as Sentry from '@sentry/nextjs';
import toast from 'react-hot-toast';
import { OrderSource } from '~/components/common/text-to-order-modal/types';
import { FileTypeEnum, UserOrg } from '~/lib/drizzle/type';
import { getFileExtensionFromMimeType } from './media';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_URL ?? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`;
export const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;

export const isProduction = process.env.NEXT_PUBLIC_BASE_URL === 'https://app.sayzolo.com';
export const isUAT = process.env.NEXT_PUBLIC_BASE_URL === 'https://uat.sayzolo.com';

export type AnyFunc = (...args: any[]) => any;

export const arrIntersection = (arr1: string[], arr2: string[]) => {
  return arr1.filter((el) => arr2.includes(el)).length > 0;
};

export const capitalize = (str: string) => (str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : str);

export const parseStringArr = (str: string) =>
  str
    .replace(/[\[\]]/g, '')
    .split(',')
    .map((x: string) => x.trim());

export const parsePrice = (num: number) => parseFloat(num.toFixed(2));

export const formatNumberToFourDp = (num?: number | null) => {
  if (num === null || num === undefined || !isFinite(num)) return '0.00';

  const numStr = num.toString();
  const [, decimalPart] = numStr.split('.');

  if (!decimalPart) return num.toFixed(2);
  // Format based on the length of the decimal  part
  if (decimalPart.length > 2) return num.toFixed(4);
  return num.toFixed(2);
};

export const AutoCode = () => {
  // Define your AutoCode logic here if needed
  return 'autocode'; // Replace with your actual AutoCode logic
};

export const getFilenameFromS3Url = (url: string) => decodeURIComponent(url.split('/')[url.split('/').length - 1] || '');

export const handleOpenFile = (file: File) => {
  if (!file) {
    alert('Please select a file first');
    return;
  }

  // Create a URL for the file
  const fileURL = URL.createObjectURL(file);
  if (file.type.includes('pdf') || file.type.includes('image')) {
    // Open the file in a new tab
    window.open(fileURL, '_blank', 'noopener,noreferrer');
  } else {
    // file is to be downloaded
    // Create a temporary anchor element
    const a = document.createElement('a');

    // Set the download attribute with a custom filename
    a.href = fileURL;
    a.download = file.name; // Set your custom filename here

    // Append the anchor to the document body and trigger a click
    document.body.appendChild(a);
    a.click();

    // Clean up: Remove the anchor from the document
    document.body.removeChild(a);

    // Revoke the object URL after the file has been downloaded to release memory
    URL.revokeObjectURL(fileURL);
  }
};

export const isTlf = (orgId?: string) =>
  orgId === process.env.NEXT_PUBLIC_TLF_ORG_ID ||
  orgId === process.env.NEXT_PUBLIC_LJH_ORG_ID ||
  orgId === process.env.NEXT_PUBLIC_VWF_ORG_ID ||
  orgId === process.env.NEXT_PUBLIC_JOKIA_ORG_ID ||
  orgId === process.env.NEXT_PUBLIC_HOC_ORG_ID ||
  orgId === process.env.NEXT_PUBLIC_HAISIA_ORG_ID;

export const isTlfUser = (orgId?: string) => orgId === process.env.NEXT_PUBLIC_TLF_ORG_ID;

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function processPromisesBatch<T, R = any>(
  items: Array<R>,
  limit: number,
  fn: (item: R) => Promise<T>,
  progressCallback?: (progress: number) => void
): Promise<T[]> {
  let results: T[] = [];
  for (let start = 0; start < items.length; start += limit) {
    const end = start + limit > items.length ? items.length : start + limit;

    const slicedResults = await Promise.all(items.slice(start, end).map(fn));
    progressCallback?.(((start + limit) / items.length) * 100);

    results = [...results, ...slicedResults];
  }

  return results;
}

export function moveElementToFront(arr: any[], index: number) {
  if (index > -1) {
    const temp = arr[index];
    // Remove the element from its current position
    arr.splice(index, 1);
    // Add the element to the beginning of the array
    arr.unshift(temp);
  }

  return arr;
}

export const formatPhoneNumber = (phoneNumber: string) => {
  // Remove any non-digit characters except the '+' sign
  const cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');

  // Apply the format +XX XXXX XXXX XXX
  return cleanedNumber.replace(/(\+\d{2})(\d{4})(\d{4})(\d*)/, '$1 $2 $3 $4');
};

const isZoloAPIServer = (url: string) => {
  try {
    const host = new URL(url).host;
    return apiBaseUrl?.includes(host) || host.endsWith('.sayzolo.com');
  } catch (e) {
    // If URL constructor fails, it means the URL does not have a host
    return false;
  }
}

interface FetchWrapperOptions {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: object;
  header?: Record<string, string>;
  token?: string | null;
  /** Show error toast on failure (default: true) */
  showErrorToast?: boolean;
  showToast?: boolean;
  /** Show success toast on 2xx response (default: false) */
  showSuccessToast?: boolean;
  /** Throw error on non-2xx response instead of returning null (default: false) */
  throwOnError?: boolean;
}

export async function fetchWrapper<T>({
  url,
  method,
  body,
  header = {},
  token = null,
  showErrorToast = true,
  showToast,
  showSuccessToast = false,
  throwOnError = false,
}: FetchWrapperOptions): Promise<T | null> {
  const isZoloAPI = isZoloAPIServer(url);
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: getHeaders(method, header, token, isZoloAPI),
      body: method !== 'GET' ? JSON.stringify(body) : undefined,
    });

  } catch (error) {
    handleNetworkError(error, method, url, showErrorToast && showToast, throwOnError);
    if (throwOnError) throw error;
    return null;
  }

  const result = await parseResponse<T>(response);
  if (!response.ok) {
    await handleError({
      response,
      result,
      method,
      url,
      body,
      showErrorToast: !!(showErrorToast && showToast),
      throwOnError,
    });
    return null;
  }
  handleSuccess(result, showSuccessToast);
  return result;
}

function getHeaders(
  method: string,
  headers: Record<string, string>,
  token: string | null,
  isZoloAPI: boolean
): HeadersInit {
  return {
    ...(method !== 'GET' && { 'Content-Type': 'application/json' }),
    ...headers,
    ...(isZoloAPI && token && { Authorization: `Bearer ${token}` }),
  };
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    return response.json();
  }
  if (contentType?.includes('text/')) {
    return response.text() as T;
  }
  if (contentType?.includes('spreadsheetml')) {
    return response.blob() as unknown as T;
  }
  return response.text() as T;
}

async function handleError({
  response,
  result,
  method,
  url,
  body,
  showErrorToast,
  throwOnError,
}: {
  response: Response;
  result: any;
  method: string;
  url: string;
  body?: object;
  showErrorToast: boolean;
  throwOnError: boolean;
}) {
  // Sentry logging
  Sentry.withScope((scope) => {
    scope.setFingerprint([method, url, method !== 'GET' ? JSON.stringify(body) : '']);
    Sentry.captureEvent({
      message: result?.message,
      extra: { request: body, response },
    });
  });

  if (response.status === 401) return; // Suppress 401 errors

  // Prioritize validation errors
  const errorMessage = result?.error_code === 'API_VALIDATION_ERROR'
    ? result?.errors?.[0]?.message || result?.message || 'Invalid data provided'
    : result?.message || `Request failed with status ${response.status}`;

  if (showErrorToast && method !== 'GET') {
    toast(errorMessage, { icon: 'ℹ️' });
  }

  if (throwOnError) {
    throw new Error(errorMessage);
  }
}

function handleSuccess(result: any, showSuccessToast: boolean) {
  if (showSuccessToast && result?.message) {
    toast.success(result.message);
  }
}

function handleNetworkError(
  error: Error,
  method: string,
  url: string,
  showErrorToast: boolean,
  throwOnError: boolean
) {
  if (method !== 'GET' && showErrorToast) {
    toast.error(`Something went wrong: ${error.message}`);
  }

  Sentry.withScope((scope) => {
    scope.setFingerprint([method, url]);
    Sentry.captureException(error);
  });

  if (throwOnError) throw error;
}

export async function retry<T>(fn: AnyFunc, retryTimes = 1, retryInterval = 3000, predicate?: (res: T) => boolean): Promise<T> {
  try {
    const result = await fn();
    if (predicate ? predicate(result) : true) {
      return result;
    } else {
      throw new Error('Predicate failed', { cause: result });
    }
  } catch (e) {
    if (retryTimes <= 0) {
      throw e;
    } else {
      await sleep(retryInterval);
      return retry(fn, retryTimes - 1, retryInterval, predicate);
    }
  }
}

export function ignoreCaseCompare(str1: string, str2: string) {
  return str1.localeCompare(str2, undefined, { sensitivity: 'base' }) === 0;
}

export function filenameToOrderSource(filename: string): OrderSource {
  const lowerCaseFilename = filename.toLocaleLowerCase();
  // temporary, will add more if audio
  if (lowerCaseFilename.endsWith('pdf')) {
    return 'pdf';
  } else if (lowerCaseFilename.endsWith('jpg') || lowerCaseFilename.endsWith('jpeg') || lowerCaseFilename.endsWith('png')) {
    return 'image';
  } else if (lowerCaseFilename.endsWith('xls') || lowerCaseFilename.endsWith('xlsx')) {
    return 'excel';
  } else {
    return 'manual';
  }
}

export function changeFileName(file: File, newFileName: string) {
  return new File([file], newFileName, {
    type: file.type, // Preserve the original file type
    lastModified: file.lastModified, // Preserve the last modified date
  });
}

export function parseQueryParams(params: Record<string, any>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([_, v]) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      })
      .map(([key, value]) => {
        if (value instanceof Date && !isNaN(value.getTime())) {
          return [key, value.toISOString()];
        }
        return [key, value];
      })
  );
}

export function constructUrlSearchParams(params: Record<string, string>): URLSearchParams {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Append each item in the array as a separate parameter
      value.forEach((item) => searchParams.append(key, item));
    } else {
      searchParams.append(key, value);
    }
  });

  return searchParams;
}

export function getCountryCode(phoneNumber: string): string | undefined {
  const countryCodes: { [key: string]: string } = {
    AF: '+93',
    AL: '+355',
    DZ: '+213',
    AS: '+1-684',
    AD: '+376',
    AO: '+244',
    AI: '+1-264',
    AQ: '+672',
    AG: '+1-268',
    AR: '+54',
    AM: '+374',
    AW: '+297',
    AU: '+61',
    AT: '+43',
    AZ: '+994',
    BS: '+1-242',
    BH: '+973',
    BD: '+880',
    BB: '+1-246',
    BY: '+375',
    BE: '+32',
    BZ: '+501',
    BJ: '+229',
    BM: '+1-441',
    BT: '+975',
    BO: '+591',
    BA: '+387',
    BW: '+267',
    BR: '+55',
    IO: '+246',
    BN: '+673',
    BG: '+359',
    BF: '+226',
    BI: '+257',
    CV: '+238',
    KH: '+855',
    CM: '+237',
    CA: '+1',
    KY: '+1-345',
    CF: '+236',
    TD: '+235',
    CL: '+56',
    CN: '+86',
    CX: '+61',
    CC: '+61',
    CO: '+57',
    KM: '+269',
    CD: '+243',
    CG: '+242',
    CK: '+682',
    CR: '+506',
    HR: '+385',
    CU: '+53',
    CW: '+599',
    CY: '+357',
    CZ: '+420',
    DK: '+45',
    DJ: '+253',
    DM: '+1-767',
    DO: '+1-809',
    EC: '+593',
    EG: '+20',
    SV: '+503',
    GQ: '+240',
    ER: '+291',
    EE: '+372',
    SZ: '+268',
    ET: '+251',
    FK: '+500',
    FO: '+298',
    FJ: '+679',
    FI: '+358',
    FR: '+33',
    GA: '+241',
    GM: '+220',
    GE: '+995',
    DE: '+49',
    GH: '+233',
    GI: '+350',
    GR: '+30',
    GL: '+299',
    GD: '+1-473',
    GP: '+590',
    GU: '+1-671',
    GT: '+502',
    GG: '+44-1481',
    GN: '+224',
    GW: '+245',
    GY: '+592',
    HT: '+509',
    HM: '+672',
    VA: '+39-06',
    HN: '+504',
    HK: '+852',
    HU: '+36',
    IS: '+354',
    IN: '+91',
    ID: '+62',
    IR: '+98',
    IQ: '+964',
    IE: '+353',
    IM: '+44-1624',
    IL: '+972',
    IT: '+39',
    JM: '+1-876',
    JP: '+81',
    JE: '+44-1534',
    JO: '+962',
    KZ: '+7',
    KE: '+254',
    KI: '+686',
    KP: '+850',
    KR: '+82',
    KW: '+965',
    KG: '+996',
    LA: '+856',
    LV: '+371',
    LB: '+961',
    LS: '+266',
    LR: '+231',
    LY: '+218',
    LI: '+423',
    LT: '+370',
    LU: '+352',
    MO: '+853',
    MG: '+261',
    MW: '+265',
    MY: '+60',
    MV: '+960',
    ML: '+223',
    MT: '+356',
    MH: '+692',
    MQ: '+596',
    MR: '+222',
    MU: '+230',
    YT: '+262',
    MX: '+52',
    FM: '+691',
    MD: '+373',
    MC: '+377',
    MN: '+976',
    ME: '+382',
    MS: '+1-664',
    MA: '+212',
    MZ: '+258',
    MM: '+95',
    NA: '+264',
    NC: '+687',
    NZ: '+64',
    NI: '+505',
    NE: '+227',
    NG: '+234',
    NU: '+683',
    NF: '+672',
    MP: '+1-670',
    NO: '+47',
    OM: '+968',
    PK: '+92',
    PW: '+680',
    PS: '+970',
    PA: '+507',
    PG: '+675',
    PY: '+595',
    PE: '+51',
    PH: '+63',
    PN: '+64',
    PL: '+48',
    PT: '+351',
    PR: '+1-787',
    QA: '+974',
    RE: '+262',
    RO: '+40',
    RU: '+7',
    RW: '+250',
    BL: '+590',
    SH: '+290',
    KN: '+1-869',
    LC: '+1-758',
    MF: '+590',
    PM: '+508',
    VC: '+1-784',
    WS: '+685',
    SM: '+378',
    ST: '+239',
    SA: '+966',
    SN: '+221',
    RS: '+381',
    SC: '+248',
    SL: '+232',
    SG: '+65',
    SX: '+1-721',
    SK: '+421',
    SI: '+386',
    SJ: '+47',
    SB: '+677',
    SO: '+252',
    ZA: '+27',
    GS: '+500',
    SS: '+211',
    ES: '+34',
    LK: '+94',
    SD: '+249',
    SR: '+597',
    SE: '+46',
    CH: '+41',
    SY: '+963',
    TW: '+886',
    TJ: '+992',
    TZ: '+255',
    TH: '+66',
    TG: '+228',
    TK: '+690',
    TO: '+676',
    TT: '+1-868',
    TN: '+216',
    TR: '+90',
    TM: '+993',
    TC: '+1-649',
    TV: '+688',
    UG: '+256',
    UA: '+380',
    AE: '+971',
    GB: '+44',
    US: '+1',
    UY: '+598',
    UZ: '+998',
    VU: '+678',
    VE: '+58',
    VN: '+84',
    WF: '+681',
    EH: '+212',
    YE: '+967',
    ZM: '+260',
    ZW: '+263',
  };

  // Remove any non-digit characters except for the leading '+'
  const cleanedNumber = phoneNumber.replace(/(?!^\+)\D/g, '');

  for (const [country, code] of Object.entries(countryCodes)) {
    if (cleanedNumber.startsWith(code.replace('+', ''))) {
      return country; // Return the country code
    }
  }

  return undefined; // Return null if no country code is found
}

export const breakArrIntoChunks = (array: any[], chunkSize: number) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

export const isMobileDevice = (): boolean => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

export const removeDuplicates = (array: (string | number | null | undefined)[] | null) => {
  const cleanArr = array.filter(Boolean);
  return cleanArr.filter((item, index) => cleanArr.indexOf(item) === index);
};

export function darkenRgba(rgbaString: string, increment = 0.2) {
  // Use a regular expression to match the RGBA values
  const rgbaMatch = rgbaString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d\.]+)?\)/);

  if (!rgbaMatch) {
    throw new Error('Invalid RGBA string');
  }

  // Extract the red, green, blue, and alpha values
  const r = parseInt(rgbaMatch[1]);
  const g = parseInt(rgbaMatch[2]);
  const b = parseInt(rgbaMatch[3]);
  const a = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1; // Default to 1 if no alpha is provided

  // Calculate the new alpha value
  const newAlpha = Math.min(a + increment, 1); // Ensure it does not exceed 1

  // Return the new RGBA string
  return `rgba(${r}, ${g}, ${b}, ${newAlpha})`;
}

export const parseInterval = (intervalString: string) => {
  // Define a mapping from PostgreSQL interval units to date-fns units
  const intervalMapping = {
    days: 'days',
    day: 'days',
    weeks: 'weeks',
    week: 'weeks',
    months: 'months',
    month: 'months',
    years: 'years',
    year: 'years',
  };

  // Split the interval string to get the amount and the unit
  const intervalParts = intervalString.trim().split(' ');
  const quantity = parseInt(intervalParts[0]);
  const unit = intervalMapping[intervalParts[1]] as string;
  return {
    quantity,
    unit,
  };
};

export const generateSimpleUuid = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

export function encodeUrlAfterLastSlash(url: string) {
  // Find the position of the last '/'
  const lastSlashIndex = url.lastIndexOf('/');

  // If no '/' is found, return the original URL
  if (lastSlashIndex === -1) {
    return url;
  }

  // Split the URL into the part before and after the last '/'
  const beforeSlash = url.substring(0, lastSlashIndex + 1);
  const afterSlash = url.substring(lastSlashIndex + 1);

  // Encode the part after the last '/'
  const encodedAfterSlash = encodeURIComponent(afterSlash);

  // Combine the unencoded and encoded parts
  return beforeSlash + encodedAfterSlash;
}

export function populateTemplate(template: string, data: Record<string, string | number | Date>) {
  return template.replace(/{(\w+)}/g, (_, key) => `${data[key]}` || `{${key}}`);
}

export function safeJsonParse<T>(str: string | null | undefined): T | null {
  if (!str) return null;
  try {
    const jsonValue: T = JSON.parse(str);

    return jsonValue;
  } catch {
    return null;
  }
};

export function formatToMaxFourDecimals(num: number) {
  // Convert the number to string and split by decimal point
  const parts = num.toString().split('.');

  // Check if the number has a decimal part and its length is 4 or more
  if (parts.length === 2 && parts[1].length >= 4) {
    return parseFloat(num.toFixed(4)); // Format to 4 decimal places
  }

  return num; // Return the original number
}

export function removeNullString(data: any): any {
  if (Array.isArray(data)) {
    return data
      .map(item => item === 'null' ? null : removeNullString(item));
  }

  if (typeof data === 'object' && data !== null) {
    const result = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (value === 'null') {
        result[key] = null; // Set to null and skip recursion
      } else {
        result[key] = removeNullString(value); // Recurse for non-'null' values
      }
    });
    return result;
  }

  // Replace 'null' strings at the primitive level
  return data === 'null' ? null : data;
}

export async function createFileFromUrl(fileUrl: string) {
  const response = await fetch(fileUrl);
  const blob = await response.blob();

  // Extract filename from URL
  const urlParts = fileUrl.split("/");
  const filename = urlParts[urlParts.length - 1]; // Get last part of URL

  // Extract file extension
  const extension = filename.split(".").pop().toLowerCase();

  // Mapping of extensions to MIME types
  const mimeTypes = {
    "pdf": "application/pdf",
    "jpg": "image/jpeg",
    "jpeg": "image/jpeg",
    "png": "image/png",
    "gif": "image/gif",
    "webp": "image/webp",
    "txt": "text/plain",
    "csv": "text/csv",
    "json": "application/json",
    "xml": "application/xml",
    "html": "text/html",
    "css": "text/css",
    "js": "application/javascript",
    "mp3": "audio/mpeg",
    "mp4": "video/mp4",
    "zip": "application/zip",
    "rar": "application/x-rar-compressed",
    "7z": "application/x-7z-compressed",
    "doc": "application/msword",
    "docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "xls": "application/vnd.ms-excel",
    "xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "ppt": "application/vnd.ms-powerpoint",
    "pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  };

  // Determine content type based on file extension
  const contentType = mimeTypes[extension] || "application/octet-stream"; // Default if unknown

  // Create a File object with the correct filename and content type
  const file = new File([blob], filename, { type: contentType });
  return file;
}

export const uploadFile = async (file: File, organization?: UserOrg | null): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('prefix', `order/attachments/${organization?.id || 'unidentified_org'}/${Date.now()}`);
  const resp = await fetch('/api/file', {
    method: 'POST',
    body: formData,
  });
  if (resp.ok) {
    return (await resp.json()).url as string;
  } else {
    throw new Error('Error uploading file, please try again');
  }
};

export const base64ToFile = (base64Data: string, filename: string, mimeType: string = 'application/octet-stream') => {
  // Remove data URL prefix if it exists
  const base64WithoutPrefix = base64Data.replace(/^data:.*,/, '');

  // Convert base64 to binary
  const binaryString = window.atob(base64WithoutPrefix);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Create blob from binary data
  const blob = new Blob([bytes], { type: mimeType });

  const convertedFileName = convertFileName(filename, mimeType);

  // Create File object
  return new File([blob], convertedFileName, { type: mimeType });
};

export const convertFileName = (filename: string, mimeType: string): string => {
  const fileExtension = filename.split('.').pop() || getFileExtensionFromMimeType(mimeType);

  const now = Date.now();
  // Convert file name if it is empty
  let cleanedFilename = filename.trim();
  if (!cleanedFilename) {
    cleanedFilename = `file-${now}.${fileExtension}`;
  }

  // Convert file name if it has url encoding
  const urlEncodedPattern = /%[0-9A-Fa-f]{2}/;

  if (urlEncodedPattern.test(filename)) {
    return `file-${now}.${fileExtension}`;
  }

  return cleanedFilename;
}

/**
 * Checks if mouse event coordinates are within an element's bounding box
 */
export function isWithinBoundingBox(
  event: MouseEvent | React.MouseEvent,
  elementRef: React.RefObject<HTMLElement>
): boolean {
  if (!elementRef.current) return false;

  const rect = elementRef.current.getBoundingClientRect();
  const { clientX, clientY } = event;

  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
}

export const getFileTypeFromFileName = (fileName: string): FileTypeEnum => {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    // File has no extension
    return 'document';
  }

  const extension = fileName.substring(lastDotIndex + 1).toLowerCase();
  return extension === 'jpg' || extension === 'jpeg' || extension === 'png' ? FileTypeEnum.IMAGE : FileTypeEnum.DOCUMENT;
};

export const replaceFileExtension = (fileName: string, newExtension: string): string => {
  // Handle empty filename
  if (!fileName) return '';

  // Remove leading dot from newExtension if present
  const cleanNewExtension = newExtension.startsWith('.')
    ? newExtension.slice(1)
    : newExtension;

  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    // File has no extension - append new extension
    return `${fileName}.${cleanNewExtension}`;
  }

  // Replace everything after the last dot
  return `${fileName.substring(0, lastDotIndex)}.${cleanNewExtension}`;
};